import React, { useEffect, useMemo, useRef } from "react";
import _ from "lodash";
import numeral from "numeral";
import ReactDataGrid from "react-data-grid";
import CircularProgress from "@material-ui/core/CircularProgress";

import { GridWrapper, CustomRowContainer, StyledCustomCell, StyledCustomCellWrapper } from "./sourceFileFieldStyles";

export const DefaultCellFormatter = ({ value }) => {
  const numToStrWithDelimeter = typeof value === "number" ? numeral(value).format("0,0.00") : value;

  return <div>{numToStrWithDelimeter}</div>;
};

const CustomCellRenderer = props => {
  const { value, colIdx } = props;
  const numToStrWithDelimeter = typeof value === "number" ? numeral(value).format("0,0.00") : value;

  return (
    <StyledCustomCellWrapper {...props}>
      <StyledCustomCell colIdx={colIdx}>{numToStrWithDelimeter}</StyledCustomCell>
    </StyledCustomCellWrapper>
  );
};

const calculateRowValueByColumns = (startLeftColIndex, selectedRow, dataSelectDirection, dataColumnAnchorIndex) => {
    let totalValueInRange = 0;
    let leftBoundIdx = dataSelectDirection === "ltr" ? dataColumnAnchorIndex : dataColumnAnchorIndex + startLeftColIndex - dataColumnAnchorIndex;
    const rightBoundIdx = dataSelectDirection === "ltr" ? dataColumnAnchorIndex + startLeftColIndex - dataColumnAnchorIndex : dataColumnAnchorIndex;
    while (leftBoundIdx <= rightBoundIdx) {
      totalValueInRange = totalValueInRange + selectedRow[leftBoundIdx];
      leftBoundIdx = leftBoundIdx + 1;
    }

    return totalValueInRange;
};

const RowRenderer = ({ renderBaseRow, ...props }) => {
  const { idx, row, columns, anchorCol, dataSelectDirection, rowsSelected, setSourceFileFieldSelections, dataGridRows, filteredSelectedRows } = props;

  const currentRow = rowsSelected?.find(ris => ris.filteredRowsSelectedIndex === idx);
  if (currentRow?.filteredRowsSelectedIndex !== idx ||
                (filteredSelectedRows && filteredSelectedRows.length > 0 &&
                !filteredSelectedRows?.includes(currentRow?.filteredRowsSelectedIndex))) {
    return <div className={currentRow?.filteredRowsSelectedIndex === null ? "row-selected not-bold" : "row-not-selected"}>{renderBaseRow(props)}</div>;
  }

  const colIndexSelected = currentRow.dataColumnAnchorIndex + currentRow.dataColumnOffset;

  const handleColSelection = (colIdx) => {
    if (
      (currentRow.dataSelectDirection === "ltr" && (colIdx < currentRow.dataColumnAnchorIndex || colIdx > currentRow.dataColumnAnchorIndex + currentRow.numExpectedPeriods - 1)) ||
      (currentRow.dataSelectDirection === "rtl" && (colIdx > currentRow.dataColumnAnchorIndex || colIdx < currentRow.dataColumnAnchorIndex - currentRow.numExpectedPeriods + 1))
    ) {
      return;
    }
    const selectedRow = dataGridRows[currentRow.filteredRowsSelectedIndex].row;
    const totalValueInRange = calculateRowValueByColumns(colIdx, selectedRow, currentRow.dataSelectDirection, currentRow.dataColumnAnchorIndex);

    if (colIdx === colIndexSelected) {
        if(rowsSelected && rowsSelected.length === 1){
          setSourceFileFieldSelections({ rowsSelected: [{ ...currentRow,
                                                          dataColumnOffset: null,
                                                          amountOfCellsSelected: 1,
                                                          fieldValue: calculateRowValueByColumns(currentRow.dataColumnAnchorIndex, selectedRow, currentRow.dataSelectDirection, currentRow.dataColumnAnchorIndex)
                                                        }
                                                       ],
                                                       isRangeChanged: true,
                                                       amountOfCellsSelected: 1});
        } else {
          setSourceFileFieldSelections({ rowsSelected: rowsSelected?.filter(rs => rs.filteredRowsSelectedIndex !== currentRow.filteredRowsSelectedIndex), isRangeChanged: true});
        }
    } else {
      setSourceFileFieldSelections({ rowsSelected: [...rowsSelected?.filter(rs => rs.filteredRowsSelectedIndex !== currentRow.filteredRowsSelectedIndex),
                                                                    { ...currentRow, dataColumnOffset: colIdx - currentRow.dataColumnAnchorIndex,
                                                                      amountOfCellsSelected: Math.abs(colIdx - currentRow.dataColumnAnchorIndex) + 1,
                                                                      fieldValue: totalValueInRange
                                                                    }],
                                     isRangeChanged: true
      });
    }
  };

  return (
    <CustomRowContainer>
      {columns.map(col => (
        <CustomCellRenderer
          value={row[col.key]}
          key={col.key}
          colIdx={Number(col.key)}
          width={col.width}
          anchorCol={anchorCol}
          dataSelectDirection={dataSelectDirection}
          colIndexSelected={colIndexSelected}
          onClick={() => handleColSelection(Number(col.key))}
        />
      ))}
    </CustomRowContainer>
  );
};

const DataGrid = props => {
  const {
    dataGridRows,
    dataGridColumns,
    filteredRows,
    dataColumnAnchorIndex,
    dataColumnOffset,
    rowIndexSelected,
    rowAnchor1ColumnIndex,
    rowAnchor2ColumnIndex,
    rowsSelected,
    filteredRowsSelectedIndex,
    drawerMinWidth = "800px",
    isGridLoading,
    setSourceFileFieldSelections,
    numExpectedPeriods,
    dataSelectDirection,
    setDrawerSelections,
    isPreview,
    filteredSelectedRows
  } = props;
  const fullTableDrawerWidth = useMemo(() => dataGridColumns.reduce((accum, col) => accum + col.width, 0) + 15, [dataGridColumns]);
  const gridRef = useRef(null);

  // scroll into view selected row
  useEffect(() => {
    if (gridRef.current && !isPreview) {
      const minSelectedRowIdx = rowsSelected?.sort((a, b) => a.rowIndexSelected - b.rowIndexSelected) ?? 1;
      const top = (minSelectedRowIdx - 1) * 55 + gridRef.current.getRowOffsetHeight() + 10;
      const gridCanvas = gridRef.current.getDataGridDOMNode().querySelector(".react-grid-Canvas");
      gridCanvas.scrollTop = top;
    }
  }, [gridRef.current, filteredRows]);

  useEffect(() => {
    const handleResize = _.debounce(() => {
      if (window.innerWidth < Number(drawerMinWidth.slice(0, -2)) && window.innerWidth >= 800) {
        setDrawerSelections({ drawerMinWidth: `${window.innerWidth}px` });
      } else if (window.innerWidth > Number(drawerMinWidth.slice(0, -2)) && window.innerWidth < fullTableDrawerWidth) {
        setDrawerSelections({ drawerMinWidth: `${window.innerWidth}px` });
      } else {
        setDrawerSelections({ drawerMinWidth: `${fullTableDrawerWidth}px` });
      }
    }, 300);
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [drawerMinWidth]);

  useEffect(() => {
    if (dataGridColumns?.length) {
      let drawerWidth = fullTableDrawerWidth;
      const minWidth = Number(drawerMinWidth.slice(0, -2));
      if (drawerWidth < minWidth) {
        drawerWidth = minWidth;
      }
      if (drawerWidth > window.innerWidth) {
        drawerWidth = window.innerWidth;
      }
      setDrawerSelections({ drawerMinWidth: `${drawerWidth}px` });
    }
  }, [dataGridColumns]);

  useEffect(() => {
    const filteredSelectedRowsLocal = filteredSelectedRows;
    for (let i = 0; i < filteredRows.length; i = i + 1) {
      const foundFilteredRow = rowsSelected?.find(ris => ris.rowIndexSelected === filteredRows[i].rowIndex);
      if (foundFilteredRow) {
        if(filteredSelectedRows?.indexOf(i) > -1)
        {
          continue;
        }
        foundFilteredRow.filteredRowsSelectedIndex = i;
        const filterList = rowsSelected.filter(rs => rs.rowIndexSelected !== filteredRows[i].rowIndex);
        filteredSelectedRowsLocal.push(i);
        setSourceFileFieldSelections({ rowsSelected: [...filterList, foundFilteredRow ], filteredSelectedRows: [...new Set(filteredSelectedRowsLocal)]});
      }
    }

  }, [filteredRows, rowsSelected]);

  const handleRowClick = rowIdx => {
    if (
      (filteredRows[rowIdx].row[rowAnchor1ColumnIndex] === "" || filteredRows[rowIdx].row[rowAnchor1ColumnIndex] == null) &&
      (filteredRows[rowIdx].row[rowAnchor2ColumnIndex] === "" || filteredRows[rowIdx].row[rowAnchor2ColumnIndex] == null)
    ) {
      return;
    }
    const rowAnchor1Value = filteredRows[rowIdx].row[rowAnchor1ColumnIndex] !== null && filteredRows[rowIdx].row[rowAnchor1ColumnIndex] !== undefined ?
      filteredRows[rowIdx].row[rowAnchor1ColumnIndex].toString() :
      "";
    const rowAnchor2Value = filteredRows[rowIdx].row[rowAnchor2ColumnIndex] !== null && filteredRows[rowIdx].row[rowAnchor2ColumnIndex] !== undefined ?
      filteredRows[rowIdx].row[rowAnchor2ColumnIndex].toString() :
      "";

    const newRowIndexSelected = filteredRows[rowIdx].rowIndex;
    if (!rowsSelected?.find(ris => ris.rowIndexSelected === rowIdx)) {

    const startLeftIndex = 2;
    const selectedRow = dataGridRows[newRowIndexSelected].row;
    const totalValueInRange = calculateRowValueByColumns(startLeftIndex, selectedRow, dataSelectDirection, dataColumnAnchorIndex);
    setSourceFileFieldSelections({
      isRangeChanged: true,
      rowsSelected: [ ...rowsSelected ?? [],
        {
          rowIndexSelected: newRowIndexSelected,
          dataSelectDirection,
          dataColumnAnchorIndex,
          numExpectedPeriods,
          filteredRowsSelectedIndex: rowIdx,
          dataColumnOffset: -numExpectedPeriods + 1,
          amountOfCellsSelected: numExpectedPeriods,
          rowAnchor1Value,
          rowAnchor2Value,
          fieldValue: totalValueInRange,
          rowAnchor1ColumnIndex,
          rowAnchor2ColumnIndex
        }]
      });
    }
    else {
      setSourceFileFieldSelections({
        isRangeChanged: true,
        rowsSelected: rowsSelected?.filter(rs => rs.rowIndexSelected !== rowIdx)
      });
    }
  };

  if (isGridLoading) {
    return (
      <div style={{ paddingTop: "3em", display: "flex", justifyContent: "center", width: "100%" }}>
        <CircularProgress style={{ color: "#005AA9" }} />
      </div>
    );
  }

  if (!dataGridRows.length || !dataGridColumns.length) {
    return null;
  }

  return (
    Boolean(dataGridRows.length) &&
    Boolean(dataGridColumns.length) && (
      <GridWrapper rowSelected={filteredRowsSelectedIndex}>
        <ReactDataGrid
          ref={gridRef}
          minWidth={drawerMinWidth}
          headerRowHeight={40}
          rowHeight={55}
          columns={dataGridColumns}
          rowGetter={i => filteredRows[i]?.row}
          rowsCount={filteredRows.length}
          rowRenderer={
            <RowRenderer
              anchorCol={dataColumnAnchorIndex}
              dataSelectDirection={dataSelectDirection}
              setSourceFileFieldSelections={setSourceFileFieldSelections}
              rowsSelected = {rowsSelected}
              dataGridRows = {dataGridRows}
              filteredSelectedRows = {filteredSelectedRows}
            />
          }
          onRowClick={!isPreview && handleRowClick}
          rowIndexSelected={filteredRowsSelectedIndex}
        />
      </GridWrapper>
    )
  );
};

export default DataGrid;
